import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  getUserNotifications,
  getUnreadNotificationsCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '../../utils/notificationsService';
import NotificationItem from './NotificationItem/NotificationItem';
import NotificationsHeader from './NotificationsHeader/NotificationsHeader';
import NotificationsPagination from './NotificationsPagination/NotificationsPagination';
import Loading from '../layout/Loading';
import EmptyState from './EmptyState/EmptyState';
import { getNotificationQuickFilterQuery } from '../../constants/notificationsQuickFilters';
import './NotificationsList.css';

/** Czas animacji chowania kafelka (ms) — zgodny z CSS transition */
const NOTIFICATION_REMOVE_ANIM_MS = 260;

function NotificationsList({
  isActive = false,
  onNavigateTab = null,
  quickFilterId = 'all',
  onClearQuickFilter = null,
}) {
  const pageSize = 20;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserIdRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  /** Usunięcie z tej karty — realtime DELETE i tak nadejdzie; pomijamy duplikat liczników */
  const recentlyDeletedByClientRef = useRef(new Set());
  /** Następne ładowanie strony po zmianie currentPage bez pełnoekranowego „loading” (np. pusta strona po usunięciu) */
  const silentPageLoadRef = useRef(false);
  const prevQuickFilterRef = useRef(quickFilterId);
  const skipPageEffectOnceRef = useRef(false);
  const notificationsTabEverFocusedRef = useRef(false);
  const prevIsActiveRef = useRef(isActive);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const loadNotificationsRef = useRef(null);

  const refreshUnreadCount = useCallback(async () => {
    if (!currentUserIdRef.current) return;

    const { count, error } = await getUnreadNotificationsCount(currentUserIdRef.current);
    if (error) {
      console.error('Error loading unread notifications count:', error);
      return;
    }

    setUnreadCount(count || 0);
  }, []);

  // Load notifications
  const loadNotifications = useCallback(async (page = 1, options = {}) => {
    const silent = options.silent === true;
    if (!currentUserIdRef.current) return;

    try {
      if (!silent) setLoading(true);
      const targetPage = page;
      const { read, types } = getNotificationQuickFilterQuery(quickFilterId);
      const { data, error, total } = await getUserNotifications(currentUserIdRef.current, {
        page: targetPage,
        pageSize,
        read,
        types,
      });

      if (error) {
        console.error('Error loading notifications:', error);
        return;
      }

      setNotifications(data || []);
      
      if (total !== null && total !== undefined) {
        setTotalCount(total);
        setTotalPages(Math.ceil(total / pageSize) || 1);
      }

      await refreshUnreadCount();
    } catch (err) {
      console.error('Error in loadNotifications:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pageSize, refreshUnreadCount, quickFilterId]);

  loadNotificationsRef.current = loadNotifications;

  // Sesja — bez zależności od loadNotifications (żeby zmiana filtra nie resetowała całej inicjalizacji)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setLoading(false);
        return;
      }
      setCurrentUser(user);
      currentUserIdRef.current = user.id;
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Ładowanie listy: zmiana filtra → strona 1; zmiana strony paginacji → fetch; unik podwójnego fetcha po setCurrentPage(1)
  useEffect(() => {
    if (!currentUser?.id) return;

    const filterBumped = prevQuickFilterRef.current !== quickFilterId;
    if (filterBumped) {
      prevQuickFilterRef.current = quickFilterId;
      if (currentPage !== 1) {
        skipPageEffectOnceRef.current = true;
        setCurrentPage(1);
      }
      void loadNotifications(1);
      return;
    }

    if (skipPageEffectOnceRef.current) {
      skipPageEffectOnceRef.current = false;
      return;
    }

    const silent = silentPageLoadRef.current;
    if (silent) silentPageLoadRef.current = false;
    void loadNotifications(currentPage, { silent });
  }, [currentUser?.id, quickFilterId, currentPage, loadNotifications]);

  // Ponowne wejście na zakładkę powiadomień (bez zależności od filtra / paginacji — unik podwójnego fetcha przy zmianie filtra)
  useEffect(() => {
    if (!currentUser?.id) return;
    const wentActive = prevIsActiveRef.current === false && isActive === true;
    prevIsActiveRef.current = isActive;
    if (!wentActive) return;
    if (!notificationsTabEverFocusedRef.current) {
      notificationsTabEverFocusedRef.current = true;
      return;
    }
    void loadNotificationsRef.current(currentPageRef.current, { silent: true });
  }, [isActive, currentUser?.id]);

  // Subscribe to real-time updates when notifications tab is active
  useEffect(() => {
    if (!isActive || !currentUser?.id) return;

    const userId = currentUser.id;
    const notificationsChannel = supabase
      .channel(`user_notifications_${userId}`)
      .on('postgres_changes', {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (payload.eventType === 'DELETE' && payload.old?.id) {
          const deletedId = payload.old.id;
          if (recentlyDeletedByClientRef.current.has(deletedId)) {
            recentlyDeletedByClientRef.current.delete(deletedId);
            window.dispatchEvent(
              new CustomEvent('notificationsChanged', { detail: { userId, payload } }),
            );
            return;
          }
          const wasUnread = payload.old.read === false;
          setNotifications((prev) => prev.filter((n) => n.id !== deletedId));
          setTotalCount((tc) => {
            const next = Math.max(0, tc - 1);
            setTotalPages(Math.max(1, Math.ceil(next / pageSize)));
            return next;
          });
          if (wasUnread) {
            setUnreadCount((u) => Math.max(0, u - 1));
          }
          window.dispatchEvent(
            new CustomEvent('notificationsChanged', { detail: { userId, payload } }),
          );
          return;
        }

        if (payload.eventType === 'UPDATE' && payload.new?.id) {
          const updated = payload.new;
          const id = updated.id;
          let removedFromUnreadList = false;

          setNotifications((prev) => {
            const idx = prev.findIndex((n) => n.id === id);
            if (idx === -1) {
              return prev;
            }
            const prevRow = prev[idx];
            const merged = { ...prevRow, ...updated };

            if (quickFilterId === 'unread' && merged.read === true) {
              removedFromUnreadList = true;
              const next = prev.filter((n) => n.id !== id);
              queueMicrotask(() => {
                setCurrentPage((p) => {
                  if (next.length !== 0 || p <= 1) return p;
                  silentPageLoadRef.current = true;
                  return p - 1;
                });
              });
              return next;
            }

            return prev.map((n) => (n.id === id ? merged : n));
          });

          if (removedFromUnreadList) {
            setTotalCount((tc) => {
              const next = Math.max(0, tc - 1);
              setTotalPages(Math.max(1, Math.ceil(next / pageSize)));
              return next;
            });
          }

          void refreshUnreadCount();

          window.dispatchEvent(
            new CustomEvent('notificationsChanged', { detail: { userId, payload } }),
          );
          return;
        }

        if (payload.eventType === 'INSERT') {
          loadNotifications(currentPage, { silent: true });
          window.dispatchEvent(new CustomEvent('notificationsChanged', {
            detail: { userId, payload },
          }));
          return;
        }

        loadNotifications(currentPage, { silent: true });

        window.dispatchEvent(new CustomEvent('notificationsChanged', {
          detail: { userId, payload },
        }));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(notificationsChannel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, currentUser?.id, currentPage, quickFilterId]);

  // Handle mark as read
  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    
    const { success, error } = await markNotificationAsRead(notificationId);
    
    if (success) {
      if (quickFilterId === 'unread') {
        setNotifications((prev) => {
          const next = prev.filter((n) => n.id !== notificationId);
          queueMicrotask(() => {
            setCurrentPage((p) => {
              if (next.length !== 0 || p <= 1) return p;
              silentPageLoadRef.current = true;
              return p - 1;
            });
          });
          return next;
        });
        setTotalCount((tc) => {
          const next = Math.max(0, tc - 1);
          setTotalPages(Math.max(1, Math.ceil(next / pageSize)));
          return next;
        });
      } else {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notificationId ? { ...notif, read: true } : notif,
          ),
        );
      }

      await refreshUnreadCount();

      if (currentUserIdRef.current) {
        window.dispatchEvent(new CustomEvent('notificationsChanged', {
          detail: { userId: currentUserIdRef.current, action: 'mark_as_read' },
        }));
      }
    } else {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (!currentUserIdRef.current) return;

    const { success, error } = await markAllNotificationsAsRead(currentUserIdRef.current);
    
    if (success) {
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setUnreadCount(0);
      // Emituj event żeby zaktualizować licznik
      window.dispatchEvent(new CustomEvent('notificationsChanged', {
        detail: { userId: currentUserIdRef.current, action: 'mark_all_as_read' }
      }));
    } else {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Handle delete notification
  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    
    const { success, error } = await deleteNotification(notificationId);
    
    if (success) {
      if (!notifications.some((n) => n.id === notificationId)) {
        return;
      }

      const deletedNotification = notifications.find((notif) => notif.id === notificationId);

      recentlyDeletedByClientRef.current.add(notificationId);
      window.setTimeout(() => {
        recentlyDeletedByClientRef.current.delete(notificationId);
      }, 2500);

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, _removing: true } : n)),
      );

      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      setTotalCount((tc) => {
        const next = Math.max(0, tc - 1);
        setTotalPages(Math.max(1, Math.ceil(next / pageSize)));
        return next;
      });

      if (currentUserIdRef.current) {
        window.dispatchEvent(new CustomEvent('notificationsChanged', {
          detail: { userId: currentUserIdRef.current, action: 'delete' }
        }));
      }

      window.setTimeout(() => {
        setNotifications((prev) => {
          const next = prev.filter((n) => n.id !== notificationId);
          queueMicrotask(() => {
            setCurrentPage((p) => {
              if (next.length !== 0 || p <= 1) return p;
              silentPageLoadRef.current = true;
              return p - 1;
            });
          });
          return next;
        });
      }, NOTIFICATION_REMOVE_ANIM_MS);
    } else {
      console.error('Error deleting notification:', error);
    }
  };

  if (loading) {
    return <Loading size="medium" />;
  }

  if (notifications.length === 0) {
    const filterActive = quickFilterId !== 'all';
    return (
      <EmptyState
        filterActive={filterActive}
        onClearFilter={filterActive ? onClearQuickFilter : null}
      />
    );
  }

  return (
    <div className="notifications-list">
      <NotificationsHeader 
        unreadCount={unreadCount}
        onMarkAllAsRead={handleMarkAllAsRead}
      />

      <div className="notifications-items">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={handleMarkAsRead}
            onDelete={handleDelete}
          />
        ))}
      </div>

      <NotificationsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        loading={loading}
        onPageChange={(nextPage) => {
          if (!Number.isInteger(nextPage)) return;
          if (nextPage < 1 || nextPage > totalPages) return;
          if (nextPage === currentPage) return;
          setCurrentPage(nextPage);
        }}
      />
    </div>
  );
}

export default NotificationsList;
